/**
 * 启动页上「最近的项目」那一块此刻是什么状态。
 *
 * # 为什么这是一个状态机，而不是几个 if
 *
 * 因为它要区分的两件事在数据上长得几乎一样，而在用户那里差着一整个决定：
 *
 *   **读不到** → 我的项目可能还在，只是这会儿取不回来。该做的是重试。
 *   **没有**   → 我确实一个项目都没有。该做的是建一个。
 *
 * 设计稿 Z10 把这条单独标出来并写了一句很重的话：
 * 「写反了用户会以为项目丢了」。
 *
 * 而写反它太容易了——只要后端在读失败时回一个空数组，或者前端把
 * `data ?? []` 当成默认值，两种情况就在代码里合流了，
 * 之后**没有任何地方能再把它们分开**。所以这一层的存在本身就是那条规则：
 * 把「取数失败」与「取回来是空的」在类型上分开，让它们没法合流。
 *
 * SPEC-routes-api 对后端也提了同一条要求：
 * 「读失败返回错误，不返回空数组 —— 前端要能区分「读不到」和「没有」」。
 */

export type RecentProjectsState =
  /** 正在读。不放灰色骨架假装有六个。 */
  | { readonly kind: 'loading' }
  /** 读失败。文案是「暂时读不到项目列表 · 重试」。 */
  | { readonly kind: 'failed' }
  /** 真的一个都没有。文案是「还没有项目。可以先聊，也可以现在建一个。」 */
  | { readonly kind: 'empty' }
  /** 搜了但没匹配上。与 `empty` 是两回事：库里有项目，只是这个词搜不到。 */
  | { readonly kind: 'no_match'; readonly keyword: string }
  | { readonly kind: 'ready'; readonly projects: readonly RecentProject[] }

export interface RecentProject {
  readonly id: string
  readonly name: string
  /**
   * 同步状态。**`null` 表示没核验过**，界面上写「未核验」——
   * 不用绿点也不填 0。设计稿把这一条单独写出来，因为一个绿点
   * 和一个 0 都是在替后端做一个它没给出的判断。
   */
  readonly synced: boolean | null
}

/** 设计稿：最多列 6 个。 */
export const RECENT_LIMIT = 6

export interface RecentProjectsInput {
  readonly isLoading: boolean
  readonly failed: boolean
  /** 取回来的项目。**取数失败时必须是 null，不是空数组。** */
  readonly projects: readonly RecentProject[] | null
  readonly keyword: string
}

export function resolveRecentProjects({
  isLoading,
  failed,
  projects,
  keyword,
}: RecentProjectsInput): RecentProjectsState {
  // 失败排在最前面。放在后面的话，一个「失败且 projects 为 null」的组合
  // 会先撞上下面的空判断，然后显示成「还没有项目」——正是要防的那件事。
  if (failed) return { kind: 'failed' }
  if (isLoading) return { kind: 'loading' }
  // projects 为 null 而又没报失败，说明调用方把两种情况合流了。
  // 这里不猜，按读不到处理：宁可让用户点一次重试，也不告诉他项目没了。
  if (projects === null) return { kind: 'failed' }

  const trimmed = keyword.trim()
  if (trimmed === '') {
    if (projects.length === 0) return { kind: 'empty' }
    return { kind: 'ready', projects: projects.slice(0, RECENT_LIMIT) }
  }

  // 搜索只做包含匹配，**不做模糊匹配**：设计稿明说不硬凑几个不相关的塞给你。
  const matched = projects.filter((p) => p.name.includes(trimmed))
  if (matched.length === 0) return { kind: 'no_match', keyword: trimmed }
  return { kind: 'ready', projects: matched.slice(0, RECENT_LIMIT) }
}
